import React from "react";
import {
	SEGMENT_CMC,
	URL_CMC_AIRCRAFT_DATA,
	URL_CMC_AIRCRAFT_HISTORY_DATA,
	URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
	URL_CMC_AIRFRAME_MRO_EVENT_DATA,
	URL_DATA_FEEDS,
} from "../constants";
import { Hero } from "../sections/hero/Hero";
import { ProudToServe } from "../sections/proud-to-serve/ProudToServe";
import { Technology } from "../sections/Technology";
import { Testimonials } from "../../common/layout/testimonials/Testimonials";
import { ProductTabs } from "../sections/ProductTabs";
import { ProductSlider } from "../sections/ProductSlider";
import { Upgrades } from "../sections/Upgrades";
import { Trial } from "../sections/Trial";
import { Pricing } from "../sections/pricing/Pricing";
import { Faq } from "../sections/faq/Faq";
import { productSegmentPageProps } from "../../common/types/product";

export default function CommercialAircraftAccidentsIncidentsData({
	packageName,
	subscribeLink,
	tabsNav,
	testimonials,
}: productSegmentPageProps) {
	return (
		<>
			<Hero
				title={packageName}
				subtitle="The Accidents and Incidents data offers a comprehensive overview of accidents and incidents by operator and/or aircraft variant."
				packages={[
					{
						label: SEGMENT_CMC,
						features: [
							"Global coverage of aircraft accidents and incidents on a tail, operator, and airport level since the late 1950’s",
							"Accident/Incident information data is provided by the Flight Safety Foundation and mapped with ch-aviation fleet data",
						],
						subscribeLinkUrl: subscribeLink,
					},
				]}
			/>
			{tabsNav}
			<ProudToServe />
			<ProductTabs
				title={packageName}
				tabs={[
					{
						title: "Aircraft Accidents and Incidents",
						description:
							"Aircraft Accidents and Incidents Data is available on a tail, operator and airport level; and includes info such as:",
						list: [
							"Aircraft accident or incident event overview (including date and time of the event, aircraft manufacturer, variant, and registration)",
							"Operator insights (name of the operator, country, and type)",
							"Aircraft insights (the exact model of aircraft, age, C/N, MSN, production line)",
							"Engine details (which engines are powering the aircraft, including engine type and subtype)",
							"Aircraft status (whether the aircraft was written off after the event)",
							"Casualties (including total casualties and total occupants on the plane)",
							"Flight profile (including flight nature, flight phase, and event coordinates)",
							"Event location (airport and country where the event happened)",
							"Origin and destination (airport name and country for both origin and destination)",
							"Maps (accident and incident event location displayed on the map)",
							"Event profile (including damage type, occurrence category, loss category, and subcategory)",
							"Remarks and source (detailed explanation of the accident and incident event and the source)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-profile-overview.jpg",
						},
					},
				]}
			/>
			<ProductSlider
				description="Search commercial aviation accidents and incidents by operator or aircraft variant, casualties, flight phase, location, damage, loss category/subcategory, and more."
				slides={[
					{
						title: "Accident/Incident",
						description:
							"Search for aircraft accident and incident based on:",
						list: [
							"Write Off (if the aircraft was written off after an accident or incident)",
							"Casualties ",
							"Occupants",
							"Flight nature (passenger, cargo, military, etc.)",
							"Damage (minor, substantial, none, etc.)",
							"Flight phase (landing, takeoff, climb, etc.)",
							"Occurrence Category (accident, incident, etc.)",
							"Loss category (act of nature, accidental act, etc.)",
							"Loss subcategory (turbulence, bird strike, etc.)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-category.jpg",
							title: "List of all minor cargo accidents",
						},
					},
					{
						title: "Location",
						description:
							"Search for aircraft accidents and incidents by location:",
						list: [
							"Operator location (continent, country, region)",
							"Accident/incident event location (continent, country, airport Location)",
							"Flight origin (continent, country, airport location)",
							"Flight destination (continent, country, airport location)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-location.jpg",
							title: "Aircraft accidents in France involving flights from North America",
						},
					},
					{
						title: "Operator",
						description:
							"Search for aircraft accidents and incidents by:",
						list: ["Operator name", "Operator type"],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-operator.jpg",
							title: "Complete list of accidents and incidents operated by American Airlines",
						},
					},
					{
						title: "Aircraft Type",
						description:
							"Search for aircraft accidents and incidents by:",
						list: [
							"Aircraft market group",
							"Aircraft manufacturer",
							"Aircraft production line",
							"Aircraft family",
							"Aircraft variant",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-aircraft-type.jpg",
							title: "All accidents and incidents involving Embraer regional turboprops",
						},
					},
					{
						title: "Aircraft",
						description:
							"Search for aircraft accidents and incidents by:",
						list: [
							"Aircraft registration",
							"Aircraft line/serial number",
							"Aircraft block number",
							"Aircraft age",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-aircraft.jpg",
							title: "All accidents and incidents involving aircraft aged between 15 and 25 years",
						},
					},
					{
						title: "Engine",
						description:
							"Search for aircraft accidents and incidents by:",
						list: [
							"Engine manufacturer",
							"Engine family",
							"Engine master series",
							"Engine type/subtype",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-engine.jpg",
							title: "List of all accidents and incidents involving aircraft powered by General Electric engines",
						},
					},
					{
						title: "Dates",
						description:
							"Search for aircraft accidents and incidents by:",
						list: [
							"Accident date",
							"Date built (search for aircraft built within a selected date range)",
							"Delivery date (search for aircraft delivered within a selected date range)",
						],
						visual: {
							url: "/static/img/product/commercial-aircraft-accidents-and-incidents-data/accidents-and-incidents-search-by-dates.jpg",
							title: "All accidents and incidents from 2020 to present, involving aircraft built in 2005",
						},
					},
				]}
			/>
			<Upgrades
				packageName={packageName}
				modules={[
					{
						title: "Aircraft Data",
						content:
							"Get access to our extensive fleet database with over 82,000 aircraft currently being tracked .",
						url: URL_CMC_AIRCRAFT_DATA,
					},
					{
						title: "Aircraft History Data",
						content:
							"Get access to full historical operator data down to an aircraft level.",
						url: URL_CMC_AIRCRAFT_HISTORY_DATA,
					},
					{
						title: "Aircraft Ownership Data",
						content:
							"Get insights into aircraft ownership, management, valuation estimates, full lessor portfolios, and SPV-financed aircraft details.",
						url: URL_CMC_AIRCRAFT_OWNERSHIP_DATA,
					},
					{
						title: "Airframe MRO Events Data",
						content:
							"Get insights into over 25,000 airframe maintenance events worldwide.",
						url: URL_CMC_AIRFRAME_MRO_EVENT_DATA,
					},
				]}
			>
				<Trial subscribeLink={subscribeLink} />
			</Upgrades>
			<Testimonials testimonials={testimonials} />
			<Technology />
			<Pricing
				packageName={packageName}
				description="Gain a comprehensive understanding of the aviation industry with detailed, up-to-date insights."
				list={[
					"Global coverage of aircraft accidents and incidents on a tail, operator, and airport level since the late 1950s",
					"Loss Categories and subcategories incorporated for all events, as of 2013",
				]}
				subscribeLink={subscribeLink}
				downloadsDescription="Our Commercial Aviation Aircraft Accidents and Incidents Data is ready for instant, unlimited downloads either as Excel, CSV or JSON file."
			>
				<Trial subscribeLink={subscribeLink} />
			</Pricing>
			<Faq
				items={[
					{
						title: "Can I download this data set?",
						content:
							"The Commercial Aviation Aircraft Accident and Incident Data includes an optional feature available for purchase that allows unlimited downloads in CSV, XLS, or JSON formats. To learn more about data downloads, please contact our sales team.",
						linkUrl: "mailto:sales@ch-aviation.com",
					},
					{
						title: "Can I integrate this data as a data feed into my own system?",
						content: (
							<p>
								Yes, Aircraft Accident and Incident Data can be
								purchased as a data feed for seamless
								integration into your own system. ch-aviation
								also provides full support for the integration
								process. To learn more about data feeds, click{" "}
								<a
									href={URL_DATA_FEEDS}
									target="_blank"
									rel="noopener noreferrer"
								>
									here
								</a>
								.
							</p>
						),
					},
					{
						title: "What's the data source?",
						content:
							"This module launched in cooperation with the Flight Safety Foundation. It features a comprehensive overview of accidents and incidents on an aircraft tail number level since the late 1950s. In addition, we have also incorporated detailed loss categories and subcategories for all events as of 2013, allowing you to get a deeper understanding of each incident or accident.",
					},
					{
						title: "What are the main loss categories ch-aviation is tracking?",
						content: (
							<ul className="ticks ticks--small">
								<li>Accident</li>
								<li>Incident</li>
								<li>Serious incident</li>
								<li>Unknown occurrence</li>
								<li>Unlawful interference</li>
							</ul>
						),
					},
				]}
			/>
		</>
	);
}
